import { NavLink, useParams } from "react-router-dom";
import { routePaths } from "../constants/routes";
import { getOrderByInvoiceNumber } from "../services/orderService";
import "../style/order_History.css";
import "../style/payment_page.css";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const trackingSteps = [
  { title: "Ordered", icon: "fa-shopping-cart", offset: 0 },
  { title: "Order ready", icon: "fa-box-open", offset: 30 },
  { title: "Shipped", icon: "fa-truck", offset: 180 },
  { title: "Out for delivery", icon: "fa-shipping-fast", offset: 480 },
  { title: "Delivered", icon: "fa-box", offset: 720 },
];

const getStatusIndex = (status) =>
  status === "Processing" ? 1 : trackingSteps.findIndex((step) => step.title === status);

export default function OrderTrackingPage() {
  const { invoiceNumber } = useParams();
  const order = getOrderByInvoiceNumber(invoiceNumber);

  if (!order) {
    return (
      <main className="history-container">
        <section className="history-hero">
          <div>
            <p className="checkout-eyebrow">Tracking</p>
            <h1>Order not found</h1>
            <p>Open tracking from a valid invoice or order history entry.</p>
          </div>
        </section>
      </main>
    );
  }

  const statusIndex = getStatusIndex(order.status);

  const formatTimeDate = (date) =>
    date.toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  return (
    <main className="history-container">
      <section className="history-hero">
        <div>
          <p className="checkout-eyebrow">Tracking</p>
          <h1>Track Order #{order.invoiceNumber}</h1>
          <p>Use this page to validate delivery status, order timeline, and post-purchase visibility.</p>
        </div>
        <div className="history-stats">
          <strong>{order.status}</strong>
          <span>Current status</span>
        </div>
      </section>

      <div className="order-card" style={{ maxWidth: "960px", margin: "0 auto" }}>
        <div className="order-header">
          <h3>Estimated delivery</h3>
          <span className={`order-status status-${order.status.replace(/\s+/g, "-")}`}>{order.status}</span>
        </div>

        <div className="order-details">
          <div className="order-overview-grid">
            <p>
              Expected Delivery:{" "}
              {new Date(order.estimatedDeliveryDate || Date.now() + 3 * DAY_IN_MS).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </p>
            <p>Shipping To: {order.address.name}</p>
            <p>Invoice: {order.invoiceNumber}</p>
            <p>Payment: {order.paymentMethod}</p>
          </div>

          <div className="order-address-box">
            <strong>Delivery address</strong>
            <p>
              {order.address.name}, {order.address.phone}
              <br />
              {order.address.address1}
              {order.address.address2 ? `, ${order.address.address2}` : ""}
              <br />
              {order.address.city}, {order.address.state} - {order.address.pincode}
            </p>
          </div>
        </div>

        <div className="tracking-timeline">
          {trackingSteps.map((step, index) => {
            const completed = index <= statusIndex;
            const stepDate = new Date(order.date);
            stepDate.setMinutes(stepDate.getMinutes() + step.offset);

            return (
              <div key={step.title} className={`timeline-step ${completed ? "completed" : ""}`}>
                <div className="timeline-icon">
                  <i className={`fas ${completed ? "fa-check" : step.icon}`}></i>
                </div>
                <div className="timeline-content">
                  <h4>{step.title}</h4>
                  <p>{formatTimeDate(stepDate)}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "20px" }}>
          <NavLink to={routePaths.invoice(order.invoiceNumber)} className="success-btn primary">
            Open Invoice
          </NavLink>
          <NavLink to={routePaths.orderHistory} className="success-btn secondary">
            Back to Order History
          </NavLink>
        </div>
      </div>
    </main>
  );
}
